/**
 * Blocks all AFFiNE backend traffic (renderer + workers).
 * Local workspaces keep working; cloud/auth/copilot/sync never leave the machine.
 *
 * Must be imported before `@affine/core/bootstrap/browser` or `electron`.
 */

declare global {
  // eslint-disable-next-line no-var
  var __AFFINE_DISABLE_BACKEND__: boolean | undefined;
}

globalThis.__AFFINE_DISABLE_BACKEND__ = true;

const nativeFetch = globalThis.fetch.bind(globalThis);
const NativeEventSource = globalThis.EventSource;
const NativeWebSocket = globalThis.WebSocket;

const CLOUD_HOSTS = [
  'affine.fail',
  'affine.pro',
  'insider.affine.pro',
  'app.affine.pro',
  'apple.getaffineapp.com',
];

function origin(): string {
  try {
    return globalThis.location?.origin ?? 'http://localhost';
  } catch {
    return 'http://localhost';
  }
}

function toUrl(input: RequestInfo | URL): URL | null {
  try {
    if (input instanceof URL) return input;
    if (typeof input === 'string') return new URL(input, origin());
    if (typeof Request !== 'undefined' && input instanceof Request) {
      return new URL(input.url, origin());
    }
  } catch {
    return null;
  }
  return null;
}

function isBackendUrl(url: URL): boolean {
  const path = url.pathname;
  if (
    path === '/graphql' ||
    path.startsWith('/graphql/') ||
    path.startsWith('/api/') ||
    path.startsWith('/socket.io') ||
    path.startsWith('/rpc')
  ) {
    return true;
  }
  return CLOUD_HOSTS.some(
    host => url.hostname === host || url.hostname.endsWith(`.${host}`)
  );
}

function jsonResponse(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { 'content-type': 'application/json' },
  });
}

async function readGraphqlOperation(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<string | undefined> {
  const headers = new Headers(
    init?.headers ??
      (typeof Request !== 'undefined' && input instanceof Request
        ? input.headers
        : undefined)
  );
  const fromHeader = headers.get('x-operation-name');
  if (fromHeader) return fromHeader;

  let raw: string | undefined;
  if (typeof init?.body === 'string') {
    raw = init.body;
  } else if (typeof Request !== 'undefined' && input instanceof Request) {
    try {
      raw = await input.clone().text();
    } catch {
      raw = undefined;
    }
  }
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as { operationName?: string };
    return parsed.operationName;
  } catch {
    return undefined;
  }
}

function graphqlData(data: unknown): Response {
  return jsonResponse({ data });
}

function graphqlDisabled(): Response {
  return jsonResponse({
    data: null,
    errors: [
      {
        message: 'Backend disabled: this client does not call AFFiNE APIs',
        extensions: { code: 'NETWORK_ERROR', status: 503 },
      },
    ],
  });
}

function handleGraphql(operation?: string): Response {
  switch (operation) {
    case 'serverConfig':
      return graphqlData({
        serverConfig: {
          version: '0.27.0',
          baseUrl: origin(),
          name: 'Local',
          features: ['LocalWorkspace'],
          type: 'Affine',
          initialized: true,
          calendarProviders: [],
          credentialsRequirement: {
            password: { minLength: 8, maxLength: 32 },
          },
        },
      });
    case 'oauthProviders':
      return graphqlData({
        serverConfig: { oauthProviders: [] },
      });
    case 'getCurrentUser':
      return graphqlData({ currentUser: null });
    case 'getWorkspaces':
      return graphqlData({ workspaces: [] });
    default:
      return graphqlDisabled();
  }
}

function handleRest(url: URL, method: string): Response {
  if (url.pathname === '/api/auth/session' && method === 'GET') {
    return jsonResponse({ user: null });
  }
  if (url.pathname === '/api/auth/methods' && method === 'GET') {
    return jsonResponse([]);
  }
  if (url.pathname.startsWith('/api/telemetry')) {
    return jsonResponse({ ok: true });
  }
  if (method === 'GET' || method === 'HEAD') {
    return new Response('Backend disabled', { status: 404 });
  }
  return jsonResponse(
    {
      status: 503,
      code: 'NETWORK_ERROR',
      type: 'NETWORK_ERROR',
      name: 'NETWORK_ERROR',
      message: 'Backend disabled: this client does not call AFFiNE APIs',
    },
    503
  );
}

async function offlineFetch(
  input: RequestInfo | URL,
  init?: RequestInit
): Promise<Response> {
  const url = toUrl(input);
  if (!url || !isBackendUrl(url)) {
    return nativeFetch(input as RequestInfo, init);
  }

  const method = (
    init?.method ??
    (typeof Request !== 'undefined' && input instanceof Request
      ? input.method
      : 'GET')
  ).toUpperCase();

  if (url.pathname === '/graphql' || url.pathname.startsWith('/graphql/')) {
    return handleGraphql(await readGraphqlOperation(input, init));
  }

  return handleRest(url, method);
}

class OfflineEventSource extends EventTarget {
  readonly url: string;
  readonly withCredentials: boolean;
  readyState = 2;
  onerror: ((this: EventSource, ev: Event) => any) | null = null;
  onmessage: ((this: EventSource, ev: MessageEvent) => any) | null = null;
  onopen: ((this: EventSource, ev: Event) => any) | null = null;

  constructor(url: string | URL, init?: EventSourceInit) {
    super();
    this.url = String(url);
    this.withCredentials = !!init?.withCredentials;
    queueMicrotask(() => {
      const event = new Event('error');
      this.onerror?.call(this as unknown as EventSource, event);
      this.dispatchEvent(event);
    });
  }

  close() {
    this.readyState = 2;
  }

  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSED = 2;
}

class OfflineWebSocket extends EventTarget {
  static readonly CONNECTING = 0;
  static readonly OPEN = 1;
  static readonly CLOSING = 2;
  static readonly CLOSED = 3;

  readonly url: string;
  readonly protocol = '';
  readonly extensions = '';
  readonly bufferedAmount = 0;
  binaryType: BinaryType = 'blob';
  readyState = OfflineWebSocket.CLOSED;
  onopen: ((this: WebSocket, ev: Event) => any) | null = null;
  onerror: ((this: WebSocket, ev: Event) => any) | null = null;
  onclose: ((this: WebSocket, ev: CloseEvent) => any) | null = null;
  onmessage: ((this: WebSocket, ev: MessageEvent) => any) | null = null;

  constructor(url: string | URL) {
    super();
    this.url = String(url);
    queueMicrotask(() => {
      const error = new Event('error');
      this.onerror?.call(this as unknown as WebSocket, error);
      this.dispatchEvent(error);
      const close = new CloseEvent('close', {
        code: 1006,
        reason: 'backend disabled',
        wasClean: false,
      });
      this.onclose?.call(this as unknown as WebSocket, close);
      this.dispatchEvent(close);
    });
  }

  send() {}
  close() {
    this.readyState = OfflineWebSocket.CLOSED;
  }
}

function install() {
  globalThis.fetch = offlineFetch as typeof fetch;

  if (NativeEventSource) {
    globalThis.EventSource = OfflineEventSource as unknown as typeof EventSource;
  }

  if (NativeWebSocket) {
    globalThis.WebSocket = new Proxy(NativeWebSocket, {
      construct(target, args: [string | URL, ...(string | string[])[]]) {
        const url = toUrl(args[0]);
        if (url && isBackendUrl(url)) {
          return new OfflineWebSocket(args[0]);
        }
        return new target(...args);
      },
    });
  }

  const nav = globalThis.navigator;
  if (nav && typeof nav.sendBeacon === 'function') {
    const nativeBeacon = nav.sendBeacon.bind(nav);
    nav.sendBeacon = (url: string | URL, data?: BodyInit | null) => {
      const parsed = toUrl(url);
      if (parsed && isBackendUrl(parsed)) {
        return true;
      }
      return nativeBeacon(url, data);
    };
  }
}

install();
